/* eslint-disable @typescript-eslint/no-unnecessary-condition -- defensive reads of projection fields that may be absent on older bridges */
import { useStore } from '../store';
import { fmtMoney } from '../bridge';

/** Business overview: one phone-size read of where money comes from and where
 *  it goes. Everything here is already projected - ledgerMonthly (P&L with
 *  per-category income/expense), partners (signed deals), loan/credit terms.
 *  No new sim asks. Signing partners, repaying loans etc. stay in their own
 *  sections; this panel links out instead of duplicating commands. */

const human = (key: string): string => key.replace(/_/g, ' ');

export function BusinessOverviewPanel() {
  const { sim, openPanel } = useStore();
  const months = sim.ledgerMonthly ?? [];
  const last = months.at(-1);
  const recent = months.slice(-3);
  const avgNet = recent.length > 0 ? Math.round(recent.reduce((a, m) => a + m.netCents, 0) / recent.length) : 0;
  const runwayMonths = avgNet < 0 ? Math.floor(sim.cashCents / -avgNet) : null;

  const streams = last
    ? Object.entries(last.byCategory)
        .map(([key, v]) => ({ key, ...v }))
        .sort((a, b) => Math.abs(b.netCents) - Math.abs(a.netCents))
    : [];
  const incomeStreams = streams.filter((s) => s.incomeCents > 0);
  const expenseStreams = streams.filter((s) => s.expenseCents > 0);

  const partners = sim.business?.partners;

  return (
    <div style="padding:6px 14px 16px">
      <div class="eyebrow eyebrow-rule">Business overview</div>

      <div class="card" style="margin-top:10px;padding:12px 16px">
        <div class="row-label">Cash on hand</div>
        <div class="row-value" style="font-size:18px">${fmtMoney(sim.cashCents)}</div>
        {last && (
          <div class="row-label" style="margin-top:6px">
            Month {last.month + 1}: +${fmtMoney(last.incomeCents)} in, -${fmtMoney(last.expenseCents)} out, net {last.netCents >= 0 ? '+' : '-'}${fmtMoney(Math.abs(last.netCents))}
          </div>
        )}
        {recent.length > 1 && (
          <div class="row-label" style="margin-top:4px">
            Net last {recent.length} months: {recent.map((m) => `${m.netCents >= 0 ? '+' : '-'}$${fmtMoney(Math.abs(m.netCents))}`).join(' / ')}
          </div>
        )}
        {runwayMonths !== null && (
          <div class="row-label" style="margin-top:4px;color:var(--ui-amber, #b97a2a)">
            At this burn, cash covers about {runwayMonths} month{runwayMonths === 1 ? '' : 's'}.
          </div>
        )}
        {avgNet >= 0 && recent.length > 0 && (
          <div class="row-label" style="margin-top:4px">Breaking even or better at current rates.</div>
        )}
        {!last && <div class="row-label" style="margin-top:6px">The monthly ledger posts once the sim has run a full month.</div>}
      </div>

      {last && streams.length > 0 && <div class="eyebrow" style="margin-top:14px">Cash flow by stream - month {last.month + 1}</div>}
      {incomeStreams.length > 0 && (
        <div class="card" style="border:var(--ui-border);background:var(--ui-parchment-soft);margin-top:8px;padding:10px 12px">
          <div class="row-label">Income</div>
          {incomeStreams.map((s) => (
            <div key={s.key} class="row" style="margin:0;border:none;padding:4px 0">
              <span class="row-main"><div class="row-value" style="font-size:13px;text-transform:capitalize">{human(s.key)}</div></span>
              <span class="row-label">+${fmtMoney(s.incomeCents)}</span>
            </div>
          ))}
        </div>
      )}
      {expenseStreams.length > 0 && (
        <div class="card" style="border:var(--ui-border);background:var(--ui-parchment-soft);margin-top:8px;padding:10px 12px">
          <div class="row-label">Expenses</div>
          {expenseStreams.map((s) => (
            <div key={s.key} class="row" style="margin:0;border:none;padding:4px 0">
              <span class="row-main"><div class="row-value" style="font-size:13px;text-transform:capitalize">{human(s.key)}</div></span>
              <span class="row-label">-${fmtMoney(s.expenseCents)}</span>
            </div>
          ))}
        </div>
      )}

      <div class="eyebrow" style="margin-top:14px">Partners</div>
      <div class="card" style="border:var(--ui-border);background:var(--ui-parchment-soft);margin-top:8px;padding:10px 12px">
        {!partners || partners.active.length === 0 ? (
          <div class="row-label" style="text-transform:none;letter-spacing:0.01em;white-space:normal">
            No partners signed. A partner invests cash upfront, then receives a share of one revenue stream for an agreed term.
          </div>
        ) : (
          partners.active.map((p) => (
            <div key={p.id} class="row" style="margin:0;border:none;padding:4px 0">
              <span class="row-main">
                <div class="row-value" style="font-size:13px">{p.name}</div>
                <div class="row-label" style="text-transform:none;letter-spacing:0.01em">takes {(p.shareBp / 100).toFixed(p.shareBp % 100 === 0 ? 0 : 1)}% of {human(p.stream)} - {p.daysLeft}d left</div>
              </span>
              <span class="row-label">${fmtMoney(p.paidCents)}</span>
            </div>
          ))
        )}
        <button class="btn btn-quiet" style="width:100%;margin-top:8px" onClick={() => { openPanel({ kind: 'office', section: 'partners' }); }}>Open partners</button>
      </div>

      {(sim.loan || sim.loanTerms) && (
        <>
          <div class="eyebrow" style="margin-top:14px">Debt</div>
          <div class="card" style="border:var(--ui-border);background:var(--ui-parchment-soft);margin-top:8px;padding:10px 12px">
            <div class="row" style="margin:0;border:none;padding:4px 0">
              <span class="row-main"><div class="row-value" style="font-size:13px">Loan principal</div></span>
              <span class="row-label">{sim.loan ? `$${fmtMoney(sim.loan.principal)} at ${sim.loan.aprPercent}%` : 'None'}</span>
            </div>
            {sim.loanTerms && (
              <div class="row-label" style="margin-top:4px;text-transform:none;letter-spacing:0.01em;white-space:normal">
                New borrowing costs {sim.loanTerms.takeAprPercent}% a year; cap ${fmtMoney(sim.loanTerms.maxPrincipalCents)}.
              </div>
            )}
            <button class="btn btn-quiet" style="width:100%;margin-top:8px" onClick={() => { openPanel({ kind: 'office', section: 'finance' }); }}>Open finance</button>
          </div>
        </>
      )}
    </div>
  );
}
